/**
 * Strongly typed models and contracts for GARS Authentication,
 * User-Permission-Company mappings, and corporate response envelopes.
 *
 * Source of Truth: GARS Frontend Integration Guide - Authentication & User Permissions API
 */

/**
 * Canonical 7 UI-Aligned Permissions (core.gars_permissions).
 * UI controls screen visibility, company dropdowns, tabs, and action buttons
 * using these canonical permission codes.
 */
export const GARS_PERMISSIONS = {
  /** 1. Super Admin: Unrestricted access across all screens, tabs, companies, regions, and user search */
  SUPER_ADMIN: 'SUPER_ADMIN',
  /** 2. Workspace Analytics User Search: Unlocks the USER EMAIL search filter to look up other users */
  WORKSPACE_ANALYTICS_SEARCH: 'WORKSPACE_ANALYTICS_SEARCH',
  /** 3. Gemini Overview & Benchmarking: Gemini Intelligence -> Overview and Benchmarking tab */
  GEMINI_OVERVIEW_VIEW: 'GEMINI_OVERVIEW_VIEW',
  /** 4. Gemini Employee Telemetry: Gemini Intelligence -> Employee Telemetry tab (All Employees / Company & Region filters) */
  GEMINI_TELEMETRY_VIEW: 'GEMINI_TELEMETRY_VIEW',
  /** 5. License Overview: License Management -> License Overview tab */
  LICENSE_OVERVIEW_VIEW: 'LICENSE_OVERVIEW_VIEW',
  /** 6. Employee License Directory: License Management -> Employee License Directory tab */
  LICENSE_DIRECTORY_VIEW: 'LICENSE_DIRECTORY_VIEW',
  /** 7. Access & Security Management: Access & Security (Sidebar administration screen) */
  ACCESS_SECURITY_MANAGE: 'ACCESS_SECURITY_MANAGE',

  // ── Workspace Adoption Functional Capabilities (User-Level Granular Keys) ──
  /** View Workspace Adoption: Access to the Workspace Adoption module, dashboards, and metrics (Parent Permission) */
  WORKSPACE_ADOPTION_VIEW: 'WORKSPACE_ADOPTION_VIEW',
  /** External Search by Email: Search for users outside current workspace by email address (Additional Permission) */
  WORKSPACE_ADOPTION_EXTERNAL_EMAIL_SEARCH: 'WORKSPACE_ADOPTION_EXTERNAL_EMAIL_SEARCH',
  /** Export Adoption Report: Export and download Workspace Adoption reports (Additional Permission) */
  WORKSPACE_ADOPTION_REPORT_EXPORT: 'WORKSPACE_ADOPTION_REPORT_EXPORT',

  // ── Backward-compatibility aliases ──
  USER_MANAGE: 'ACCESS_SECURITY_MANAGE',
  USAGE_EXPORT: 'GEMINI_USAGE_EXPORT',
  USAGE_ALL_READ: 'SUPER_ADMIN',
  USAGE_TEAM_READ: 'GEMINI_TELEMETRY_VIEW',
  USAGE_SELF_READ: 'GEMINI_USAGE_SELF_READ',
  USAGE_DELETE: 'GEMINI_USAGE_DELETE',
  USAGE_COMPANY_SCOPED_READ: 'GEMINI_OVERVIEW_VIEW',
} as const;

export type GarsPermissionCode = (typeof GARS_PERMISSIONS)[keyof typeof GARS_PERMISSIONS];

/**
 * Numeric Database Primary Keys in core.gars_permissions (strictly 1 to 7).
 */
export const GARS_PERMISSION_IDS = {
  /** 1. Super Admin: Unrestricted global access */
  SUPER_ADMIN: 1,
  /** 2. Workspace Analytics User Search: Unlocks user email search */
  WORKSPACE_ANALYTICS_SEARCH: 2,
  /** 3. Gemini Overview & Benchmarking */
  GEMINI_OVERVIEW_VIEW: 3,
  /** 4. Gemini Employee Telemetry */
  GEMINI_TELEMETRY_VIEW: 4,
  /** 5. License Overview */
  LICENSE_OVERVIEW_VIEW: 5,
  /** 6. Employee License Directory */
  LICENSE_DIRECTORY_VIEW: 6,
  /** 7. Access & Security Management */
  ACCESS_SECURITY_MANAGE: 7,
} as const;

export const VALID_PERMISSION_IDS: readonly number[] = [1, 2, 3, 4, 5, 6, 7];

export function isValidPermissionId(id: unknown): id is number {
  const num = Number(id);
  return !isNaN(num) && Number.isInteger(num) && num >= 1 && num <= 7;
}

/**
 * Data scoping levels and structure supported by GARS authorization.
 */
export type DataScopeLevel = 'global' | 'region' | 'country' | 'company';

export interface DataScope {
  level: DataScopeLevel;
  allowedRegions: string[];
  allowedCountries: string[];
  allowedCompanies: string[];
}

/**
 * Token payload returned by POST /api/v1/auth/login.
 */
export interface GarsAuthTokenData {
  accessToken: string;
  refreshToken: string;
  tokenType?: string;
  expiresIn?: number;
  permissions?: string[];
  companyCodes?: string[];
  accessScopes?: string[];
}

/**
 * Standard ApiResponse envelope used across GARS services.
 */
export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  message: string;
  requestId?: string;
  timestamp?: string;
}

export type GarsLoginResponse = ApiResponse<GarsAuthTokenData>;

/**
 * Supported operations on POST /api/v1/gars-users/permissions.
 */
export type PermissionOperationType =
  | 'RESOLVE_USER_PERMISSIONS'
  | 'ADD_GARS_USER'
  | 'ASSIGN_USER_PERMISSION'
  | 'ASSIGN_USER_COMPANY_PERMISSION'
  | 'UPDATE_USER_PERMISSION'
  | 'UPDATE_USER_COMPANY_PERMISSION'
  | 'DELETE_GARS_USER'
  | 'DELETE_USER_PERMISSION'
  | 'DELETE_USER_COMPANY_PERMISSION';

export type GarsPermissionOperation = PermissionOperationType;

/**
 * Specific company mapping item per permission.
 */
export interface PermissionCompanyItem {
  permissionId: number;
  companyCodes?: string[];
  permission_id?: number;
  company_codes?: string[];
}

/**
 * Flexible user identifier input supporting:
 * - holcimId (or userId / user_id / holcim_id) e.g. 18627232
 * - email e.g. "alfredo.disharoontornos@holcim.com"
 * - cn (Common Name) e.g. "adisharo"
 */
export type UserIdentifierInput =
  | string
  | number
  | {
      holcimId?: number;
      email?: string;
      cn?: string;
      userId?: number;
      user_id?: number;
      holcim_id?: number;
    };

/**
 * Request payload for POST /api/v1/gars-users/permissions.
 * Supports:
 * - Exactly 1 user identifier (holcimId, email, or cn)
 * - Single assignment (permissionId, companyCode optional)
 * - Bulk assignment (permissionIds, companyCodes optional)
 * - Per-permission company assignment (permissionCompanies)
 * - Delete/revoke actions (action: 'DELETE', delete: true)
 * - Optional auditReason
 */
export interface UserCompanyPermissionRequest {
  holcimId?: number;
  email?: string;
  cn?: string;
  user_id?: number;
  userId?: number;
  holcim_id?: number;
  firstName?: string;
  lastName?: string;
  first_name?: string;
  last_name?: string;
  active?: boolean;
  is_active?: boolean;
  permissionId?: number;
  permissionIds?: number[];
  permission_id?: number;
  permission_ids?: number[];
  companyCode?: string;
  companyCodes?: string[];
  company_code?: string;
  company_codes?: string[];
  permissionCompanies?: PermissionCompanyItem[];
  permission_companies?: PermissionCompanyItem[];
  permissions?: Array<PermissionCompanyItem | GarsUserPermissionItem>;
  action?: 'ASSIGN' | 'UPDATE' | 'DELETE';
  operation?: 'ASSIGN' | 'UPDATE' | 'DELETE';
  update?: boolean;
  isUpdate?: boolean;
  is_update?: boolean;
  updateRequest?: boolean;
  updateUser?: boolean;
  update_user?: boolean;
  updateUserData?: boolean;
  update_user_data?: boolean;
  delete?: boolean;
  isDelete?: boolean;
  is_delete?: boolean;
  deleteRequest?: boolean;
  auditReason?: string;
  audit_reason?: string;
}

/**
 * Permission assignment nested directly on a user object in garsUsers response.
 */
export interface GarsUserPermissionItem {
  permissionId?: number;
  permissionCode?: string;
  permissionName?: string;
  companyCodes?: string[];
  companyCode?: string | null;
  // Aliases for compatibility
  permission_id?: number;
  permission_code?: string;
  permission_name?: string;
  company_codes?: string[];
  company_code?: string | null;
}

/**
 * Provisioned GARS user record in core.gars_users.
 */
export interface GarsUserRecord {
  id?: number;
  holcimId: number;
  email: string;
  firstName: string;
  lastName: string;
  active: boolean;
  permissions?: GarsUserPermissionItem[];
  // Aliases for compatibility
  holcim_id?: number;
  first_name?: string;
  last_name?: string;
}

/**
 * Individual user-permission-company assignment record.
 * companyCode can be null for global permissions (e.g. GEMINI_USER_MANAGE).
 */
export interface UserPermissionRecord {
  id: number;
  holcimId?: number;
  email: string;
  firstName?: string;
  lastName?: string;
  permissionId?: number;
  permissionCode?: string;
  permissionName?: string;
  companyCode?: string | null;
  companyCodes?: string[];
  companyName?: string | null;
  country?: string | null;
  region?: string | null;
  active: boolean;
  // Aliases for template & backward compatibility
  holcim_id?: number;
  first_name?: string;
  last_name?: string;
  permission_id?: number;
  permission_code?: string;
  permission_name?: string;
  company_code?: string | null;
  company_codes?: string[];
  company_name?: string | null;
  is_active?: boolean;
}

/**
 * Available permission in the master catalog.
 */
export interface PermissionCatalogItem {
  permissionId?: number;
  permissionCode?: string;
  permissionName?: string;
  description?: string;
  active?: boolean;
  // Aliases
  permission_id?: number;
  permission_code?: string;
  permission_name?: string;
}

/**
 * Canonical 7 Master Permissions catalog items matching core.gars_permissions IDs 1 to 7.
 */
export const DEFAULT_PERMISSIONS_CATALOG: PermissionCatalogItem[] = [
  {
    permissionId: 1,
    permissionCode: 'SUPER_ADMIN',
    permissionName: 'Super Admin',
    description: 'Unrestricted global access across all reporting modules, companies, and settings',
    active: true,
  },
  {
    permissionId: 2,
    permissionCode: 'WORKSPACE_ANALYTICS_SEARCH',
    permissionName: 'Workspace Analytics User Search',
    description: 'Unlocks external user email search filter in Workspace Adoption',
    active: true,
  },
  {
    permissionId: 3,
    permissionCode: 'GEMINI_OVERVIEW_VIEW',
    permissionName: 'Gemini Overview & Benchmarking',
    description: 'Access Gemini Intelligence Overview metrics, department adoption, and benchmarking views',
    active: true,
  },
  {
    permissionId: 4,
    permissionCode: 'GEMINI_TELEMETRY_VIEW',
    permissionName: 'Gemini Employee Telemetry',
    description: 'Drill down into individual employee telemetry and usage event logs in Gemini Intelligence',
    active: true,
  },
  {
    permissionId: 5,
    permissionCode: 'LICENSE_OVERVIEW_VIEW',
    permissionName: 'License Overview',
    description: 'View Google Workspace and Gemini license allocations and utilization metrics',
    active: true,
  },
  {
    permissionId: 6,
    permissionCode: 'LICENSE_DIRECTORY_VIEW',
    permissionName: 'Employee License Directory',
    description: 'View employee-level license assignments, inactivity tracking, and account statuses',
    active: true,
  },
  {
    permissionId: 7,
    permissionCode: 'ACCESS_SECURITY_MANAGE',
    permissionName: 'Access & Security Management',
    description: 'Create, modify, and manage user roles, permissions, and company data scopes',
    active: true,
  },
];

/**
 * Available company in the master catalog.
 */
export interface CompanyCatalogItem {
  companyCode?: string;
  companyName?: string;
  country?: string;
  region?: string;
  active?: boolean;
  // Aliases
  company_code?: string;
  company_name?: string;
}

/**
 * Unified Snapshot response returned by POST /api/v1/gars-users/permissions.
 */
export interface UserPermissionSnapshotResponse {
  operation?: GarsPermissionOperation | string;
  garsUsers?: GarsUserRecord[];
  userPermissions?: UserPermissionRecord[];
  permissions?: PermissionCatalogItem[];
  companies?: CompanyCatalogItem[];
  // Legacy / envelope aliases
  gars_users?: GarsUserRecord[];
  user_permissions?: UserPermissionRecord[];
  permissions_catalog?: PermissionCatalogItem[];
  companies_catalog?: CompanyCatalogItem[];
}

export type PermissionSnapshotData = UserPermissionSnapshotResponse;

/**
 * PostgreSQL constraint violation scenario types documented in README.
 */
export type GarsConstraintErrorType =
  | 'fk_gars_ucp_user'
  | 'fk_gars_ucp_permission'
  | 'fk_gars_ucp_company'
  | 'uk_gars_ucp_user_perm_company';

/**
 * Corporate JSONBase security context metadata echoed in extra_data.
 */
export interface GarsSecurityContext {
  token_verified?: boolean;
  requested_by?: string;
  on_behalf_of?: string;
  api_key_authorized?: boolean;
  audit_reason?: string;
}

/**
 * Extra data item within corporate JSONBase response.
 */
export interface GarsExtraDataItem {
  error_msg?: string;
  constraint?: GarsConstraintErrorType | string;
  security_context_1?: GarsSecurityContext;
}

/**
 * Corporate JSONBase standard response envelope.
 */
export interface CorporateJsonEnvelope<T> {
  status: 'OK' | 'NOK';
  operation: string;
  status_code: string;
  time: string;
  data: T[];
  extra_data: GarsExtraDataItem[];
}

/**
 * Normalized business error thrown when the corporate envelope returns status === 'NOK'
 * or HTTP 422 Unprocessable Entity.
 */
export class GarsBusinessError extends Error {
  constructor(
    public readonly errorMessage: string,
    public readonly constraint?: GarsConstraintErrorType | string,
    public readonly statusCode: string = '422',
    public readonly operation: string = 'RESOLVE_USER_PERMISSIONS',
    public readonly rawEnvelope?: unknown
  ) {
    super(errorMessage);
    this.name = 'GarsBusinessError';
  }

  get isDuplicate(): boolean {
    return this.constraint === 'uk_gars_ucp_user_perm_company';
  }

  get isUserNotFound(): boolean {
    return this.constraint === 'fk_gars_ucp_user';
  }

  get isPermissionNotFound(): boolean {
    return this.constraint === 'fk_gars_ucp_permission';
  }

  get isCompanyNotFound(): boolean {
    return this.constraint === 'fk_gars_ucp_company';
  }
}

/**
 * Request payload validator implementing Section 5B rules from the Integration Guide.
 * Returns null if valid, or the exact ApiResponse.message string if invalid.
 */
export function validateUserCompanyPermissionRequest(req: UserCompanyPermissionRequest): string | null {
  const isDeleteFlag =
    req.delete === true ||
    req.isDelete === true ||
    req.is_delete === true ||
    req.deleteRequest === true ||
    (req as any).delete_request === true;
  const isUpdateFlag =
    req.update === true ||
    req.isUpdate === true ||
    req.is_update === true ||
    req.updateRequest === true ||
    (req as any).update_request === true ||
    req.updateUser === true ||
    req.update_user === true ||
    req.updateUserData === true ||
    req.update_user_data === true;

  // Scenario 15: Setting both update: true and delete: true
  if (isUpdateFlag && isDeleteFlag && !req.action) {
    return 'Invalid action; cannot set both update and delete to true';
  }

  // Raw action validation
  const rawAction = req.action || req.operation;
  let effectiveAction: 'ASSIGN' | 'UPDATE' | 'DELETE' = 'ASSIGN';

  if (rawAction) {
    const upper = String(rawAction).toUpperCase().trim();
    if (upper !== 'ASSIGN' && upper !== 'UPDATE' && upper !== 'DELETE') {
      return `Invalid action (${rawAction}); allowed values are ASSIGN, UPDATE, or DELETE`;
    }
    // Conflicting action + flags
    if (
      (upper === 'ASSIGN' && (isDeleteFlag || isUpdateFlag)) ||
      (upper === 'UPDATE' && isDeleteFlag) ||
      (upper === 'DELETE' && isUpdateFlag)
    ) {
      return `Invalid action (${rawAction}); allowed values are ASSIGN, UPDATE, or DELETE`;
    }
    effectiveAction = upper as 'ASSIGN' | 'UPDATE' | 'DELETE';
  } else if (isDeleteFlag) {
    effectiveAction = 'DELETE';
  } else if (isUpdateFlag) {
    effectiveAction = 'UPDATE';
  }

  const rawUserId = req.holcimId ?? req.userId ?? req.user_id ?? req.holcim_id;
  const hasUser =
    (rawUserId !== undefined && rawUserId !== null && !isNaN(Number(rawUserId))) ||
    Boolean(req.email && String(req.email).trim().length > 0) ||
    Boolean(req.cn && String(req.cn).trim().length > 0);

  const permIds =
    req.permissionIds ??
    (req.permissionId !== undefined ? [req.permissionId] : undefined) ??
    req.permission_ids ??
    (req.permission_id !== undefined ? [req.permission_id] : undefined);

  const compCodes =
    req.companyCodes ??
    (req.companyCode !== undefined ? [req.companyCode] : undefined) ??
    req.company_codes ??
    (req.company_code !== undefined ? [req.company_code] : undefined);

  const permCompanies = req.permissionCompanies ?? req.permission_companies ?? req.permissions;

  // Scenario 11: Providing both flat permissionIds and permissionCompanies
  if (permIds && permCompanies) {
    return 'Provide either permissionIds or permissionCompanies, not both in the same request';
  }

  // Scenario 12: permissionIds cannot contain null values
  if (permIds && permIds.some((id: any) => id === null || id === undefined || isNaN(Number(id)))) {
    return 'permissionIds cannot contain null values';
  }
  if (
    permCompanies &&
    permCompanies.some((item) => {
      const id = item.permissionId ?? item.permission_id;
      return id === null || id === undefined || isNaN(Number(id));
    })
  ) {
    return 'permissionIds cannot contain null values';
  }

  // Canonical Permission ID Range Validation (core.gars_permissions IDs 1 to 7)
  if (
    permIds &&
    permIds.some((id: any) => {
      const n = Number(id);
      return !Number.isInteger(n) || n < 1 || n > 7;
    })
  ) {
    return 'Permission ID must be an integer between 1 and 7';
  }
  if (
    permCompanies &&
    permCompanies.some((item) => {
      const id = item.permissionId ?? item.permission_id;
      const n = Number(id);
      return !Number.isInteger(n) || n < 1 || n > 7;
    })
  ) {
    return 'Permission ID must be an integer between 1 and 7';
  }

  // Scenario 13: companyCodes cannot contain null or blank values
  if (
    compCodes &&
    compCodes.some((code: any) => code === null || code === undefined || String(code).trim().length === 0)
  ) {
    return 'companyCodes cannot contain null or blank values';
  }
  if (
    permCompanies &&
    permCompanies.some((item) => {
      const codes = item.companyCodes ?? item.company_codes;
      return codes && codes.some((c: any) => c === null || c === undefined || String(c).trim().length === 0);
    })
  ) {
    return 'companyCodes cannot contain null or blank values';
  }

  // Scenario 10: Providing multiple flat permissionIds together with flat companyCodes
  if (permIds && permIds.length > 1 && compCodes && compCodes.length > 0) {
    return 'Specify a single permissionId when providing companyCodes, or use permissionCompanies for multiple permissions';
  }

  // Scenario 9: Providing companyCodes without permissionIds
  if (compCodes && compCodes.length > 0 && (!permIds || permIds.length === 0)) {
    return 'Both permissionId and user are required when companyCode is provided';
  }

  // Operation specific user requirements:
  if (effectiveAction === 'UPDATE') {
    // Scenario 6
    if (!hasUser) {
      return 'A user identifier (holcimId, email, or cn) is required for update operations';
    }
    // Scenario 7
    if ((!permIds || permIds.length === 0) && (!permCompanies || permCompanies.length === 0)) {
      return 'At least one permission (permissionIds or permissionCompanies) is required for update operations';
    }
  } else if (effectiveAction === 'DELETE') {
    // Scenario 8
    if (!hasUser) {
      return 'A user identifier (holcimId, email, or cn) is required for delete operations';
    }
  } else {
    // ASSIGN
    // Scenario 5
    const hasAssignments = Boolean(
      (permIds && permIds.length > 0) ||
        (compCodes && compCodes.length > 0) ||
        (permCompanies && permCompanies.length > 0)
    );
    if (hasAssignments && !hasUser) {
      return 'A user identifier (holcimId, email, or cn) is required when assigning permissions or companies';
    }
  }

  return null;
}
